/**
 * Shared API contract types — mirrors App\Support\ApiResponse (BUILD-PLAN §42).
 *
 * Keeping these in one place means a change to the backend envelope produces
 * a compile error at every call site rather than a runtime surprise.
 */

export interface ApiSuccess<T> {
  success: true;
  message: string;
  data: T;
  meta?: Record<string, unknown>;
}

export interface ApiError {
  success: false;
  message: string;
  errors: Record<string, string[]>;
  code: ApiErrorCode;
}

/**
 * Machine-readable failure codes emitted by the backend. The frontend
 * branches on these rather than on HTTP status alone, because two different
 * conditions can share a status (a disabled feature and a genuinely missing
 * record both return 404 by design).
 */
export type ApiErrorCode =
  | 'VALIDATION_FAILED'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'RATE_LIMITED'
  | 'FEATURE_COMING_SOON'
  | 'MAINTENANCE'
  | 'SERVER_ERROR'
  | 'NETWORK_ERROR'
  | 'REQUEST_FAILED';

/**
 * The normalised error every API call rejects with.
 *
 * Axios errors, network failures and backend envelopes are all converted to
 * this shape by the client interceptor, so components never have to inspect
 * `error.response?.data?.…` defensively.
 */
export class ApiException extends Error {
  constructor(
    public readonly code: ApiErrorCode,
    message: string,
    public readonly status: number,
    public readonly errors: Record<string, string[]> = {},
  ) {
    super(message);
    this.name = 'ApiException';
  }

  /** Field-level validation messages, for binding to a form. */
  fieldErrors(): Record<string, string> {
    return Object.fromEntries(
      Object.entries(this.errors).map(([field, messages]) => [field, messages[0] ?? '']),
    );
  }

  get isComingSoon(): boolean {
    return this.code === 'FEATURE_COMING_SOON';
  }

  get isAuthError(): boolean {
    return this.code === 'UNAUTHENTICATED' || this.code === 'FORBIDDEN';
  }
}
